import { useState, useEffect, useCallback } from 'react'
import { storageManager, StorageFile } from '../lib/storage'
import { videoURLCache } from '../lib/videoCache'

export interface VideoFile extends StorageFile {
  url: string
  size: number
  duration?: number
  thumbnail?: string
}

export interface UseVideosResult {
  videos: VideoFile[]
  loading: boolean
  error: string | null
  refreshVideos: () => Promise<void>
  deleteVideo: (path: string) => Promise<void>
  getVideoUrl: (path: string) => Promise<string>
  refreshVideoUrl: (path: string) => Promise<string>
}

export function useVideos(): UseVideosResult {
  const [videos, setVideos] = useState<VideoFile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isVideoFile = (filename: string): boolean => {
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v']
    return videoExtensions.some(ext => filename.toLowerCase().endsWith(ext))
  }



  const getVideoUrl = useCallback(async (path: string): Promise<string> => {
    // Verificar cache primeiro
    const cachedUrl = videoURLCache.get(path)
    if (cachedUrl) {
      console.log(`URL cache hit para: ${path}`)
      return cachedUrl
    }

    try {
      console.log(`Gerando nova URL assinada para: ${path}`)
      // Usar URL assinada com expiração de 24 horas para melhor streaming
      const signedUrl = await storageManager.getFileUrl('videos', path, true, 86400) // 24 horas
      
      // Armazenar no cache (23 horas para renovar antes de expirar)
      videoURLCache.set(path, signedUrl, 82800)
      
      return signedUrl
    } catch (err) {
      console.error('Erro ao obter URL assinada:', err)
      // Fallback para URL pública se a assinada falhar
      try {
        console.log(`Tentando URL pública para: ${path}`)
        const publicUrl = await storageManager.getFileUrl('videos', path, false)
        
        // Cache URL pública por menos tempo (1 hora)
        videoURLCache.set(path, publicUrl, 3600)
        
        return publicUrl
      } catch (fallbackErr) {
        console.error('Erro ao obter URL pública:', fallbackErr)
        throw err
      }
    }
  }, [])

  const refreshVideos = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const files = await storageManager.listFiles('videos')
      
      // Filtrar apenas arquivos de vídeo
      const videoFiles = files.filter(file => isVideoFile(file.name))
      
      // Buscar URLs para cada vídeo
      const videosWithUrls = await Promise.all(
        videoFiles.map(async (file) => {
          try {
            const url = await getVideoUrl(file.name)
            return {
              ...file,
              url,
              size: file.metadata?.size || 0
            }
          } catch (err) {
            console.error(`Erro ao obter URL para ${file.name}:`, err)
            return {
              ...file,
              url: '',
              size: file.metadata?.size || 0
            }
          }
        })
      )

      setVideos(videosWithUrls)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
      console.error('Erro ao carregar vídeos:', err)
      setError(`Erro ao carregar vídeos: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }, [getVideoUrl])

  const refreshVideoUrl = useCallback(async (path: string): Promise<string> => {
    // Forçar renovação removendo do cache
    videoURLCache.delete(path)
    return await getVideoUrl(path)
  }, [getVideoUrl])

  const deleteVideo = useCallback(async (path: string) => {
    try {
      await storageManager.deleteFile('videos', path)
      // Remover do cache
      videoURLCache.delete(path)
      // Atualizar a lista removendo o vídeo deletado
      setVideos(prev => prev.filter(video => video.name !== path))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
      console.error('Erro ao deletar vídeo:', err)
      setError(`Erro ao deletar vídeo: ${errorMessage}`)
      throw err
    }
  }, [])

  useEffect(() => {
    refreshVideos()
  }, [refreshVideos])

  return {
    videos,
    loading,
    error,
    refreshVideos,
    deleteVideo,
    getVideoUrl,
    refreshVideoUrl
  }
}
